import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  StarIcon,
  MoreVerticalIcon,
  SendIcon,
  Edit2Icon,
  CopyIcon,
  Trash2Icon,
  TagIcon,
  ClockIcon,
} from 'lucide-react';
import { useToggleFavorite } from '../../hooks/useToggleFavorite';
import { useDuplicateTemplate } from '../../hooks/useDuplicateTemplate';
import { useDeleteTemplate } from '../../hooks/useDeleteTemplate';
import { useTemplateStore } from '../../stores/templateStore';
import type { TemplateWithCategory } from '../../../lib/types/template.types';

interface TemplateCardProps {
  template: TemplateWithCategory;
  viewMode?: 'grid' | 'list';
}

export function TemplateCard({ template, viewMode = 'grid' }: TemplateCardProps) {
  const navigate = useNavigate();
  const [menuOpen, setMenuOpen] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState(false);

  const toggleFavorite = useToggleFavorite();
  const duplicateTemplate = useDuplicateTemplate();
  const deleteTemplate = useDeleteTemplate();
  const { openSendModal } = useTemplateStore();

  const handleFavorite = (e: React.MouseEvent) => {
    e.stopPropagation();
    toggleFavorite.mutate(template.id);
  };

  const handleDuplicate = () => {
    duplicateTemplate.mutate(template.id);
    setMenuOpen(false);
  };

  const handleDelete = () => {
    if (deleteConfirm) {
      deleteTemplate.mutate(template.id);
      setDeleteConfirm(false);
      setMenuOpen(false);
    } else {
      setDeleteConfirm(true);
    }
  };

  const handleEdit = () => {
    navigate(`/templates/${template.id}/edit`);
    setMenuOpen(false);
  };

  const handleSend = () => {
    navigate(`/templates/${template.id}/bulk-send`);
  };

  // Extract preview text from content blocks
  const getPreviewText = () => {
    const textBlock = template.content.blocks.find((block) => block.type === 'text');
    if (textBlock && textBlock.type === 'text') {
      return textBlock.content;
    }
    return 'No text content';
  };

  // Format relative time
  const formatRelativeTime = (dateString?: string) => {
    if (!dateString) return 'Never';
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffMins = Math.floor(diffMs / 60000);

    if (diffMins < 60) return `${diffMins}m ago`;
    const diffHours = Math.floor(diffMins / 60);
    if (diffHours < 24) return `${diffHours}h ago`;
    const diffDays = Math.floor(diffHours / 24);
    if (diffDays < 30) return `${diffDays}d ago`;
    const diffMonths = Math.floor(diffDays / 30);
    return `${diffMonths}mo ago`;
  };

  return (
    <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-4 hover:shadow-md transition-shadow relative">
      {/* Header */}
      <div className="flex items-start justify-between mb-3">
        <div className="flex-1">
          <h3 className="font-semibold text-[#212121] mb-1 line-clamp-1">
            {template.name}
          </h3>
          {template.category && (
            <span
              className="inline-block px-2 py-1 text-xs rounded-lg font-medium"
              style={{
                backgroundColor: `${template.category.color || '#E0E0E0'}20`,
                color: template.category.color || '#757575',
              }}
            >
              {template.category.name}
            </span>
          )}
        </div>

        {/* Actions */}
        <div className="flex items-center gap-1">
          {/* Favorite Star */}
          <button
            onClick={handleFavorite}
            className="p-1 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <StarIcon
              className={`w-5 h-5 ${
                template.is_favorite
                  ? 'fill-yellow-400 text-yellow-400'
                  : 'text-gray-400'
              }`}
            />
          </button>

          {/* Menu */}
          <div className="relative">
            <button
              onClick={() => setMenuOpen(!menuOpen)}
              className="p-1 hover:bg-gray-100 rounded-lg transition-colors"
            >
              <MoreVerticalIcon className="w-5 h-5 text-gray-400" />
            </button>

            {/* Dropdown Menu */}
            {menuOpen && (
              <>
                <div
                  className="fixed inset-0 z-10"
                  onClick={() => {
                    setMenuOpen(false);
                    setDeleteConfirm(false);
                  }}
                />
                <div className="absolute right-0 mt-2 w-48 bg-white rounded-xl shadow-lg border border-gray-200 z-20 overflow-hidden">
                  <button
                    onClick={handleEdit}
                    className="w-full px-4 py-3 text-left hover:bg-gray-50 flex items-center gap-3 text-sm font-medium text-gray-700"
                  >
                    <Edit2Icon className="w-4 h-4" />
                    Edit
                  </button>
                  <button
                    onClick={handleDuplicate}
                    className="w-full px-4 py-3 text-left hover:bg-gray-50 flex items-center gap-3 text-sm font-medium text-gray-700"
                  >
                    <CopyIcon className="w-4 h-4" />
                    Duplicate
                  </button>
                  <button
                    onClick={handleDelete}
                    className={`w-full px-4 py-3 text-left hover:bg-red-50 flex items-center gap-3 text-sm font-medium ${
                      deleteConfirm ? 'text-red-600' : 'text-red-500'
                    }`}
                  >
                    <Trash2Icon className="w-4 h-4" />
                    {deleteConfirm ? 'Confirm Delete?' : 'Delete'}
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      </div>

      {/* Preview */}
      <p className="text-sm text-gray-600 mb-3 line-clamp-2">
        {getPreviewText()}
      </p>

      {/* Tags */}
      {template.tags && template.tags.length > 0 && (
        <div className="flex flex-wrap gap-1 mb-3">
          {template.tags.slice(0, 3).map((tag) => (
            <span
              key={tag}
              className="inline-flex items-center gap-1 px-2 py-1 bg-gray-100 text-gray-600 text-xs rounded-full"
            >
              <TagIcon className="w-3 h-3" />
              {tag}
            </span>
          ))}
          {template.tags.length > 3 && (
            <span className="px-2 py-1 bg-gray-100 text-gray-600 text-xs rounded-full">
              +{template.tags.length - 3}
            </span>
          )}
        </div>
      )}

      {/* Footer Stats */}
      <div className="flex items-center justify-between text-xs text-gray-500 mb-3 pt-3 border-t border-gray-100">
        <div className="flex items-center gap-1">
          <SendIcon className="w-3 h-3" />
          <span>Used {template.usage_count || 0} times</span>
        </div>
        {template.last_used_at && (
          <div className="flex items-center gap-1">
            <ClockIcon className="w-3 h-3" />
            <span>{formatRelativeTime(template.last_used_at)}</span>
          </div>
        )}
      </div>

      {/* Send Button */}
      <button
        onClick={handleSend}
        className="w-full bg-[#5B8E87] text-white py-2.5 rounded-xl hover:bg-[#4F7A74] transition-colors flex items-center justify-center gap-2 font-medium text-sm"
      >
        <SendIcon className="w-4 h-4" />
        Use Template
      </button>
    </div>
  );
}
